// scentProfile — one shared, fact-grounded reading of a fragrance's notes.
//
// Why this exists (Rob, 2026-09-27, Coach Green + Rayhaan Elixir screenshots):
//   1. The detail card repeated one fact three times (subtitle, Accords chips,
//      "Scent family: …" overview) — the card must say each thing once.
//   2. Accords must be representative of the notes actually shown. Coach Green
//      (kiwi, bergamot / rosemary, geranium / cedar, moss) was labelled
//      "Fresh · Woody · Spicy" from a legacy scent_family field with no spice
//      note in it, and the catalog accord_tags said "rose" and "floral" because
//      an old substring matcher found "rose" inside "rosemary".
//   3. The description should read like a journey through the notes:
//      "A fresh and invigorating, versatile fragrance that opens with crisp kiwi
//      and bergamot, transitions into an aromatic heart of rosemary and geranium,
//      and grounds itself in an earthy base of cedar and moss."
//
// Rules: uses ONLY the notes, seasons and occasions we store — never invents a
// fact. Matching is whole-word, longest phrase first, so "rosemary" can never
// count as "rose". Pure functions, no I/O — safe for the card, filters, ops
// backfills and tests, and the same at 1 user or 1,000,000.

// note phrase -> [primary accord, optional secondary accord]
const NOTE_ACCORDS = {
  // citrus
  bergamot: ['citrus'], lemon: ['citrus'], lime: ['citrus'], orange: ['citrus'],
  'blood orange': ['citrus'], 'bitter orange': ['citrus'], mandarin: ['citrus'],
  'mandarin orange': ['citrus'], tangerine: ['citrus'], grapefruit: ['citrus'],
  citron: ['citrus'], yuzu: ['citrus'], petitgrain: ['citrus', 'green'], neroli: ['citrus', 'floral'],
  'lemon verbena': ['citrus', 'aromatic'], verbena: ['citrus', 'aromatic'], citruses: ['citrus'], citrus: ['citrus'],
  // fruity
  kiwi: ['fruity'], apple: ['fruity'], 'green apple': ['fruity', 'green'], pear: ['fruity'],
  peach: ['fruity'], apricot: ['fruity'], plum: ['fruity'], cherry: ['fruity'], raspberry: ['fruity'],
  strawberry: ['fruity'], blackberry: ['fruity'], blackcurrant: ['fruity'], 'black currant': ['fruity'],
  cassis: ['fruity'], berries: ['fruity'], 'red berries': ['fruity'], pineapple: ['fruity'],
  mango: ['fruity'], lychee: ['fruity'], litchi: ['fruity'], melon: ['fruity'], watermelon: ['fruity', 'aquatic'],
  fig: ['fruity', 'green'], 'passion fruit': ['fruity'], passionfruit: ['fruity'], grape: ['fruity'],
  quince: ['fruity'], pomegranate: ['fruity'], guava: ['fruity'], banana: ['fruity'], date: ['fruity', 'sweet'],
  dates: ['fruity', 'sweet'], 'tropical fruits': ['fruity'], fruits: ['fruity'], fruity: ['fruity'],
  // aromatic / herbal
  mint: ['aromatic'], peppermint: ['aromatic'], spearmint: ['aromatic'], lavender: ['aromatic'],
  lavandin: ['aromatic'], rosemary: ['aromatic'], sage: ['aromatic'], 'clary sage': ['aromatic'],
  basil: ['aromatic'], thyme: ['aromatic'], artemisia: ['aromatic'], wormwood: ['aromatic'],
  absinthe: ['aromatic'], tarragon: ['aromatic'], geranium: ['aromatic', 'floral'],
  eucalyptus: ['aromatic'], juniper: ['aromatic', 'woody'], 'juniper berries': ['aromatic', 'woody'],
  herbs: ['aromatic'], 'aromatic notes': ['aromatic'], davana: ['aromatic', 'fruity'], myrtle: ['aromatic'],
  // green / fresh
  'green notes': ['green'], galbanum: ['green'], 'violet leaf': ['green'], 'fig leaf': ['green'],
  'tomato leaf': ['green'], grass: ['green'], 'green tea': ['green'], tea: ['green'], bamboo: ['green'],
  ivy: ['green'], 'mate': ['green'], cucumber: ['green', 'aquatic'], leaves: ['green'],
  // aquatic
  'sea notes': ['aquatic'], 'marine notes': ['aquatic'], 'sea salt': ['aquatic'], salt: ['aquatic'],
  'water notes': ['aquatic'], 'aquatic notes': ['aquatic'], calone: ['aquatic'], ozonic: ['aquatic'],
  'ozonic notes': ['aquatic'], seaweed: ['aquatic'], ambrox: ['amber', 'woody'],
  // floral
  rose: ['floral'], 'damask rose': ['floral'], 'turkish rose': ['floral'], 'bulgarian rose': ['floral'],
  jasmine: ['floral'], 'jasmine sambac': ['floral'], tuberose: ['floral'], gardenia: ['floral'],
  'orange blossom': ['floral'], 'ylang-ylang': ['floral'], 'ylang ylang': ['floral'], lily: ['floral'],
  'lily of the valley': ['floral'], magnolia: ['floral'], peony: ['floral'], freesia: ['floral'],
  violet: ['floral', 'powdery'], iris: ['powdery', 'floral'], orris: ['powdery', 'floral'], 'orris root': ['powdery', 'floral'],
  heliotrope: ['powdery', 'sweet'], mimosa: ['floral', 'powdery'], osmanthus: ['floral', 'fruity'],
  carnation: ['floral', 'spicy'], lotus: ['floral', 'aquatic'], 'water lily': ['floral', 'aquatic'],
  honeysuckle: ['floral'], lilac: ['floral'], narcissus: ['floral'], 'white flowers': ['floral'],
  flowers: ['floral'], 'floral notes': ['floral'], orchid: ['floral'], hibiscus: ['floral'], cyclamen: ['floral'],
  // spicy
  pepper: ['spicy'], 'black pepper': ['spicy'], 'pink pepper': ['spicy'], 'sichuan pepper': ['spicy'],
  cinnamon: ['spicy'], cardamom: ['spicy'], clove: ['spicy'], cloves: ['spicy'], nutmeg: ['spicy'],
  saffron: ['spicy'], ginger: ['spicy'], anise: ['spicy'], 'star anise': ['spicy'], coriander: ['spicy'],
  cumin: ['spicy'], caraway: ['spicy'], chili: ['spicy'], allspice: ['spicy'], elemi: ['spicy', 'balsamic'],
  spices: ['spicy'], 'spicy notes': ['spicy'], 'pimento': ['spicy'],
  // woody
  cedar: ['woody'], cedarwood: ['woody'], 'atlas cedar': ['woody'], 'virginia cedar': ['woody'],
  sandalwood: ['woody'], vetiver: ['woody', 'earthy'], guaiac: ['woody'], 'guaiac wood': ['woody'],
  cypress: ['woody'], pine: ['woody', 'green'], fir: ['woody', 'green'], birch: ['woody'],
  'cashmere wood': ['woody'], cashmeran: ['woody', 'musky'], 'woody notes': ['woody'], woods: ['woody'],
  'amberwood': ['woody', 'amber'], 'ambroxan': ['amber', 'woody'], 'iso e super': ['woody'],
  'akigalawood': ['woody'], 'teak': ['woody'], 'rosewood': ['woody'], 'agarwood': ['oud'], oud: ['oud'],
  // earthy / mossy
  moss: ['earthy'], oakmoss: ['earthy'], 'oak moss': ['earthy'], 'tree moss': ['earthy'],
  patchouli: ['earthy', 'woody'], 'earthy notes': ['earthy'], mushroom: ['earthy'], soil: ['earthy'],
  // amber / resinous
  amber: ['amber'], ambergris: ['amber'], labdanum: ['amber', 'balsamic'], benzoin: ['balsamic', 'amber'],
  'tolu balsam': ['balsamic'], 'peru balsam': ['balsamic'], balsam: ['balsamic'], styrax: ['balsamic'],
  myrrh: ['balsamic'], frankincense: ['smoky', 'balsamic'], olibanum: ['smoky', 'balsamic'],
  incense: ['smoky'], resins: ['balsamic'], resin: ['balsamic'], opoponax: ['balsamic'],
  // sweet / gourmand
  vanilla: ['vanilla'], 'bourbon vanilla': ['vanilla'], 'madagascar vanilla': ['vanilla'], vanillin: ['vanilla'],
  tonka: ['amber', 'sweet'], 'tonka bean': ['amber', 'sweet'], 'tonka beans': ['amber', 'sweet'], coumarin: ['amber', 'sweet'],
  caramel: ['gourmand'], praline: ['gourmand'], toffee: ['gourmand'], honey: ['sweet'], sugar: ['sweet'],
  'brown sugar': ['sweet'], chocolate: ['gourmand'], cacao: ['gourmand'], cocoa: ['gourmand'],
  coffee: ['gourmand'], almond: ['gourmand'], 'bitter almond': ['gourmand'], hazelnut: ['gourmand'],
  pistachio: ['gourmand'], coconut: ['gourmand'], milk: ['gourmand'], cream: ['gourmand'],
  'marshmallow': ['gourmand'], 'cotton candy': ['gourmand'], rum: ['boozy'], cognac: ['boozy'], whiskey: ['boozy'],
  whisky: ['boozy'], liquor: ['boozy'], 'sweet notes': ['sweet'],
  // musky / powdery / animalic
  musk: ['musky'], 'white musk': ['musky'], musks: ['musky'], ambrette: ['musky'], 'ambrette seed': ['musky'],
  'powdery notes': ['powdery'], suede: ['leather'], leather: ['leather'], castoreum: ['leather'],
  civet: ['musky'],
  // smoky / tobacco
  tobacco: ['tobacco'], 'tobacco leaf': ['tobacco'], smoke: ['smoky'], 'birch tar': ['smoky', 'leather'],
  tar: ['smoky'], 'guaiac smoke': ['smoky'], cade: ['smoky'],
  // long tail seen in the live catalog (2026-09-27 top-600 note census)
  'woodsy notes': ['woody'], 'white woods': ['woody'], 'white wood': ['woody'], 'dry wood': ['woody'],
  driftwood: ['woody', 'aquatic'], 'precious woods': ['woody'], 'exotic woods': ['woody'], 'blonde woods': ['woody'],
  wood: ['woody'], oak: ['woody'], mahogany: ['woody'], ebony: ['woody'], amyris: ['woody'], bark: ['woody'],
  papyrus: ['woody', 'green'], cypriol: ['woody', 'earthy'], nagarmotha: ['woody', 'earthy'], 'palo santo': ['woody', 'smoky'],
  hinoki: ['woody'], georgywood: ['woody'], clearwood: ['woody', 'earthy'], cashmirwood: ['woody'], sandal: ['woody'],
  sandalowood: ['woody'], gaiac: ['woody'], vetyver: ['woody', 'earthy'],
  aldehydes: ['aldehydic'], hedione: ['floral'], frangipani: ['floral'], tiare: ['floral'], hyacinth: ['floral'],
  hiacynth: ['floral'], marigold: ['floral'], 'sweet pea': ['floral'], wisteria: ['floral'], tulip: ['floral'],
  camellia: ['floral'], sunflower: ['floral'], bellflower: ['floral'], amaryllis: ['floral'], champaca: ['floral'],
  datura: ['floral'], petalia: ['floral'], mahonial: ['floral'], 'rose water': ['floral'], pomarose: ['floral', 'fruity'],
  'solar notes': ['floral'], chamomile: ['aromatic'], angelica: ['aromatic'], 'bay leaf': ['aromatic', 'spicy'],
  fern: ['aromatic', 'green'], oregano: ['aromatic'], marjoram: ['aromatic'], fennel: ['aromatic'], shiso: ['aromatic'],
  laurels: ['aromatic'], 'herbal notes': ['aromatic'], immortelle: ['sweet', 'aromatic'],
  lemongrass: ['citrus'], kumquat: ['citrus'], pomelo: ['citrus'], clementine: ['citrus'], 'litsea cubeba': ['citrus'],
  nectarine: ['fruity'], blueberry: ['fruity'], cranberry: ['fruity'], currant: ['fruity'], rhubarb: ['fruity', 'green'],
  rhuburb: ['fruity', 'green'], tamarind: ['fruity'], papaya: ['fruity'], pitahaya: ['fruity'], mirabelle: ['fruity'],
  grenadine: ['fruity', 'sweet'], sapodilla: ['fruity'], 'dried fruits': ['fruity', 'sweet'],
  matcha: ['green'], hay: ['green', 'sweet'], cannabis: ['green'], cactus: ['green'], clover: ['green'], olive: ['green'],
  'watery notes': ['aquatic'], 'sea water': ['aquatic'], 'mineral notes': ['aquatic'], water: ['aquatic'], ice: ['aquatic'],
  'coconut water': ['gourmand', 'aquatic'], 'coconut milk': ['gourmand'],
  cassia: ['spicy'], turmeric: ['spicy'], paprika: ['spicy'], fenugreek: ['spicy'], mace: ['spicy'], timur: ['spicy'],
  cardamon: ['spicy'],
  'white amber': ['amber'], 'black amber': ['amber'], 'light amber': ['amber'], cetalox: ['amber'], ambrofix: ['amber'],
  orcanox: ['amber'], ambrarome: ['amber'], 'oriental notes': ['amber'], 'balsamic notes': ['balsamic'],
  mastic: ['balsamic'], 'copahu balm': ['balsamic'], 'gurjan balsam': ['balsamic'], cistus: ['amber', 'balsamic'],
  vanille: ['vanilla'], vanila: ['vanilla'], myrhh: ['balsamic'],
  licorice: ['sweet'], beeswax: ['sweet'], maltol: ['sweet'], 'coca-cola': ['sweet'], 'sugar cane': ['sweet'],
  lactones: ['gourmand'], chestnut: ['gourmand'], walnut: ['gourmand'], sesame: ['gourmand'], butter: ['gourmand'],
  biscuit: ['gourmand'], popcorn: ['gourmand'], gingerbread: ['gourmand', 'spicy'], macarons: ['gourmand'],
  meringue: ['gourmand'], panacotta: ['gourmand'], 'ice cream': ['gourmand'], sorbet: ['gourmand', 'fruity'],
  rice: ['gourmand'], 'gourmand accord': ['gourmand'], truffle: ['earthy'], 'carrot seeds': ['earthy'], carrot: ['earthy'],
  costus: ['earthy', 'musky'], gin: ['boozy', 'aromatic'], champagne: ['boozy'], brandy: ['boozy'], vodka: ['boozy'],
  amaretto: ['boozy', 'gourmand'], liqueur: ['boozy'],
  helvetolide: ['musky'], ambrettolide: ['musky'], skin: ['musky'], 'animal notes': ['musky', 'leather'],
  'green accord': ['green'],
}

const FRESH = new Set(['citrus', 'fruity', 'aromatic', 'green', 'aquatic'])
const WARM = new Set(['amber', 'balsamic', 'vanilla', 'sweet', 'gourmand', 'spicy', 'woody', 'oud', 'leather', 'smoky', 'tobacco', 'boozy', 'earthy'])

const ACCORD_LABEL = {
  citrus: 'Citrus', fruity: 'Fruity', aromatic: 'Aromatic', green: 'Green', aquatic: 'Aquatic',
  floral: 'Floral', powdery: 'Powdery', spicy: 'Spicy', woody: 'Woody', earthy: 'Earthy',
  amber: 'Amber', balsamic: 'Resinous', vanilla: 'Vanilla', sweet: 'Sweet', gourmand: 'Gourmand',
  musky: 'Musky', leather: 'Leather', smoky: 'Smoky', tobacco: 'Tobacco', oud: 'Oud', boozy: 'Boozy',
  aldehydic: 'Aldehydic',
}

// adjective used in "an {adj} heart of …" / "a {adj} base of …"
const TIER_ADJ = {
  citrus: 'bright', fruity: 'juicy', aromatic: 'aromatic', green: 'green', aquatic: 'airy',
  floral: 'floral', powdery: 'powdery', spicy: 'spicy', woody: 'woody', earthy: 'earthy',
  amber: 'ambery', balsamic: 'resinous', vanilla: 'creamy', sweet: 'sweet', gourmand: 'gourmand',
  musky: 'soft musky', leather: 'leathery', smoky: 'smoky', tobacco: 'rich tobacco', oud: 'deep oud', boozy: 'boozy',
  aldehydic: 'sparkling aldehydic',
}

const PHRASES = Object.keys(NOTE_ACCORDS).sort((a, b) => b.length - a.length)
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const PHRASE_RES = PHRASES.map((p) => [p, new RegExp(`(^|[^a-z])${escapeRe(p)}([^a-z]|$)`)])

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()

/** Accords for one note, whole-word and longest-phrase-first. "Rosemary" -> aromatic, never rose. */
export function accordsForNote(note) {
  const text = norm(note)
  if (!text) return []
  let hit = null
  for (const [phrase, re] of PHRASE_RES) {
    if (re.test(text)) { hit = phrase; break }
  }
  // "Cherry Blossom", "Orange Leaf": the blossom or the leaf is the note, not the fruit.
  if (/(^|[^a-z])(blossom|blossoms|flower|flowers)([^a-z]|$)/.test(text) && !(hit && /blossom|flower/.test(hit))) return ['floral']
  if (/(^|[^a-z])(leaf|leaves)([^a-z]|$)/.test(text) && !(hit && /leaf|leaves/.test(hit))) return ['green']
  return hit ? NOTE_ACCORDS[hit] : []
}

const list = (v) => (Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? v.split(/[|,;]/) : [])
  .map((s) => String(s).trim()).filter(Boolean)

function tiers(f) {
  const top = list(f?.top_notes), mid = list(f?.mid_notes), base = list(f?.base_notes)
  const flat = top.length || mid.length || base.length ? [] : list(f?.all_notes)
  return { top, mid, base, flat }
}

function score(notes, into, order, tierRank) {
  notes.forEach((n, i) => {
    const [primary, secondary] = accordsForNote(n)
    const bump = (a, w) => {
      if (!a) return
      into[a] = (into[a] || 0) + w
      if (order[a] == null) order[a] = tierRank * 100 + i
    }
    bump(primary, 1)
    bump(secondary, 0.5)
  })
}

function rank(scores, order) {
  return Object.keys(scores).sort((a, b) => scores[b] - scores[a] || order[a] - order[b])
}

// Amber, resin (benzoin, labdanum), tonka and vanilla are one warm family.
// Scored apart, a vanilla-tonka-benzoin base split three ways and lost the
// ranking to a single cedar or a single pink pepper (Rob, 2026-10-01: 126 of his
// "Amber / Gourmand" bottles led with Amber only 5 times, Spicy 31 times).
// Tonka scores as amber in NOTE_ACCORDS; generic "sweet" (honey, sugar, hay,
// dates) stays its own accord and is never folded in.
const WARM_SWEET = ['amber', 'balsamic', 'vanilla']

function mergeWarmSweet(scores, order) {
  const members = WARM_SWEET.filter((a) => scores[a])
  if (members.length < 2) return
  const total = members.reduce((sum, a) => sum + scores[a], 0)
  // Vanilla names the family only when it is the clear lead; otherwise Amber.
  const lead = members.reduce((best, a) => (scores[a] > scores[best] ? a : best), members[0])
  const key = lead === 'vanilla' && scores.vanilla > total / 2 ? 'vanilla' : 'amber'
  const first = Math.min(...members.map((a) => order[a]))
  for (const a of members) { delete scores[a]; delete order[a] }
  scores[key] = total
  order[key] = first
}

/**
 * Representative accords from the notes, strongest first. Ties go to the note
 * that appears earlier (top before heart before base), so a fragrance that opens
 * on kiwi reads fruity before it reads anything from its drydown.
 *
 * An accord earns a place only when it carries at least half the weight of the
 * strongest one, so one pinch of pepper in a vanilla-amber pyramid is not a
 * headline. When every accord rests on a single note, they all still show.
 */
export function deriveAccords(f, max = 4) {
  const { top, mid, base, flat } = tiers(f)
  const scores = {}, order = {}
  score(top, scores, order, 0)
  score(mid, scores, order, 1)
  score(base, scores, order, 2)
  score(flat, scores, order, 0)
  mergeWarmSweet(scores, order)
  const ranked = rank(scores, order)
  if (!ranked.length) return []
  const floor = Math.max(1, scores[ranked[0]] / 2)
  return ranked.filter((a, i) => i === 0 || scores[a] >= floor).slice(0, max)
}

export const accordLabel = (a) => ACCORD_LABEL[a] || String(a || '').replace(/\b\w/g, (c) => c.toUpperCase())

/** Chips for the card: derived from notes; legacy scent family only when we hold no notes at all. */
export function accordChips(f, fallbackTokens = []) {
  const derived = deriveAccords(f)
  if (derived.length) return derived.map(accordLabel)
  return fallbackTokens
}

function dominant(notes) {
  const scores = {}, order = {}
  score(notes, scores, order, 0)
  return rank(scores, order)[0] || null
}

const joinNotes = (notes) => {
  const n = notes.slice(0, 3).map((s) => s.toLowerCase())
  if (n.length <= 1) return n.join('')
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}
const article = (word) => (/^[aeiou]/i.test(word) ? 'an' : 'a')

function character(f) {
  const { top, mid, base, flat } = tiers(f)
  const scores = {}, order = {}
  ;[top, mid, base, flat].forEach((t, i) => score(t, scores, order, i))
  let fresh = 0, warm = 0, floral = 0
  for (const [a, s] of Object.entries(scores)) {
    if (FRESH.has(a)) fresh += s
    else if (WARM.has(a)) warm += s
    else if (a === 'floral' || a === 'powdery') floral += s
  }
  const total = fresh + warm + floral
  if (!total) return null
  if (floral / total >= 0.45) return 'floral and elegant'
  if (fresh >= warm * 1.5) return 'fresh and invigorating'
  const sweetish = (scores.vanilla || 0) + (scores.sweet || 0) + (scores.gourmand || 0)
  if (warm >= fresh * 1.5) return sweetish >= warm / 2 ? 'warm and indulgent' : 'warm and grounded'
  return 'fresh yet warm'
}

const TOP_ADJ = { citrus: 'crisp', fruity: 'crisp', green: 'crisp', aquatic: 'airy', aromatic: 'cool, herbal' }

/**
 * One-sentence journey through the note pyramid, built only from stored facts.
 * Returns null when there are no notes — the card then says nothing rather than
 * padding the space with a repeated label.
 */
export function describeScentJourney(f) {
  const { top, mid, base, flat } = tiers(f)
  const char = character(f)
  if (!char) return null
  const versatile = list(f?.occasions).length >= 3 || list(f?.seasons).length >= 3
  const opener = `${article(char)} ${char}${versatile ? ', versatile' : ''} fragrance`
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

  if (flat.length) {
    return `${cap(opener)} built around ${joinNotes(flat)}.`
  }
  const parts = []
  if (top.length) {
    const d = dominant(top)
    const adj = TOP_ADJ[d] || TIER_ADJ[d]
    parts.push(`opens with ${adj ? `${adj} ` : ''}${joinNotes(top)}`)
  }
  if (mid.length) {
    const adj = TIER_ADJ[dominant(mid)]
    parts.push(`${parts.length ? 'transitions into' : 'centres on'} ${adj ? `${article(adj)} ${adj} ` : 'a '}heart of ${joinNotes(mid)}`)
  }
  if (base.length) {
    const d = dominant(base)
    const adj = TIER_ADJ[d]
    const verb = ['woody', 'earthy', 'oud', 'leather', 'smoky', 'tobacco'].includes(d) ? 'grounds itself in' : 'settles into'
    parts.push(`${parts.length ? verb : 'rests on'} ${adj ? `${article(adj)} ${adj} ` : 'a '}base of ${joinNotes(base)}`)
  }
  if (!parts.length) return null
  const body = parts.length === 1 ? parts[0] : parts.length === 2 ? `${parts[0]} and ${parts[1]}` : `${parts[0]}, ${parts[1]}, and ${parts[2]}`
  return `${cap(opener)} that ${body}.`
}

/** "Jean Paul Gaultier — Le Male Elixir" -> "Jean Paul Gaultier Le Male Elixir" */
export function inspiredByLabel(value) {
  const s = String(value || '').trim()
  if (!s) return ''
  return s.replace(/\s*[—–]\s*/g, ' ').replace(/\s+/g, ' ')
}
